import { useCallback, useEffect, useRef, useState } from "react";
import engineApi, {
  isEngineConfigured,
  isNotEnrolledError,
  isUnreachableError,
} from "../../services/engineApi";

/**
 * useEngineStatus — the tab's single connection to GET /status (SUPERBETA-7).
 *
 * Owns four things the tab used to do badly or not at all:
 *
 *  1. POLLING. One read on mount meant the delegate light, collateral, open
 *     positions and sizing floors froze at whatever they were when the tab
 *     opened. Now it re-reads every POLL_MS, and pauses entirely while the tab
 *     is hidden — polling a screen nobody is looking at spends the member's
 *     battery and the engine's rate limit for nothing.
 *
 *  2. BACKOFF. A down engine used to mean a failed request every interval,
 *     forever. Failures double the delay up to MAX_BACKOFF_MS, and the console
 *     is written to ONCE per outage, on the transition into it — not once per
 *     attempt. A tab left open overnight against a dead engine must not fill
 *     the console.
 *
 *  3. DEMO vs LIVE, as a first-class state rather than an error. Three cases,
 *     and they are NOT interchangeable:
 *       "connecting" — configured, first read still in flight. Say nothing yet.
 *       "demo"       — no base URL, or we have never had an answer. Everything
 *                      on screen is the local model and must be labelled so.
 *       "live"       — the engine has answered at least once. The values ARE
 *                      the engine's.
 *     Note what the third one means during an outage: once real values have
 *     landed, losing the connection does NOT put the tab back into demo. The
 *     numbers are still the engine's, they are just old — that is `stale`, and
 *     calling it demo would mislabel real member money as illustrative.
 *
 *  4. 404 IS NOT AN OUTAGE. Before enrollment the engine answers 404. It is
 *     reachable, there is simply no record yet: no error, no backoff, no demo.
 *
 * Read-only. Moves no money, changes no execution, arms nothing.
 */

/* 45s — inside the contract's 30–60s window. Fast enough that collateral and
   delegate status are not visibly stale, slow enough to be cheap. */
export const POLL_MS = 45000;
export const MAX_BACKOFF_MS = 300000;

/** 1st failure → POLL_MS, then doubling, capped. */
export const backoffMs = (failures) =>
  Math.min(POLL_MS * 2 ** Math.max(0, failures - 1), MAX_BACKOFF_MS);

export default function useEngineStatus({ enabled = true } = {}) {
  const configured = isEngineConfigured();
  const active = enabled && configured;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(active);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [everConnected, setEverConnected] = useState(false);
  const [failures, setFailures] = useState(0);
  const [lastUpdatedMs, setLastUpdatedMs] = useState(null);

  /* The scheduler reads the failure count synchronously right after a fetch
     settles, which is before React has committed the state update — hence the
     ref alongside the state. The state is for rendering, the ref is for the
     next delay. */
  const failureRef = useRef(0);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const warnedRef = useRef(false);
  const timerRef = useRef(null);
  /* Two reads can overlap (a forced one issued while a poll is in flight).
     Only the NEWEST request is allowed to write state, or an older answer
     landing last would quietly roll the tab back to pre-save values. */
  const seqRef = useRef(0);
  const appliedRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  const succeeded = (fresh) => {
    failureRef.current = 0;
    warnedRef.current = false;
    setFailures(0);
    setEverConnected(true);
    setError(null);
    setLastUpdatedMs(Date.now());
    setStatus(fresh);
  };

  /**
   * One read. Returns THIS call's own EngineStatus (or null), so a caller that
   * needs the engine's answer in hand — the allocation save, which has to
   * compare the echo — gets it without racing the re-render.
   *
   * `force` bypasses the de-duplication guard. The background poll sets it
   * false: a second concurrent read of the same data is waste. Anything
   * happening AFTER a write must set it true, because the guard would otherwise
   * hand back nothing (or, worse, an answer issued before the write landed) and
   * the save would report itself unconfirmed when it had in fact been stored.
   *
   * @param {{force?: boolean}} [opts]
   */
  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!active) return null;
    if (inFlightRef.current && !force) return null;
    const seq = (seqRef.current += 1);
    const isCurrent = () => mountedRef.current && seq >= appliedRef.current;
    inFlightRef.current = true;
    setRefreshing(true);
    try {
      const fresh = await engineApi.getStatus();
      if (isCurrent()) {
        appliedRef.current = seq;
        succeeded(fresh);
      }
      return fresh;
    } catch (e) {
      if (!isCurrent()) return null;
      appliedRef.current = seq;

      /* Not enrolled yet: the engine answered. Nothing is wrong and nothing is
         stale — there is just no record to show. */
      if (isNotEnrolledError(e)) {
        succeeded(null);
        return null;
      }

      failureRef.current += 1;
      setFailures(failureRef.current);
      /* An unreachable engine is a connection state the chip already states in
         full; surfacing it as a red error banner too would say the same thing
         twice, the second time as if the member had broken something. An error
         the engine DID return is theirs to see. */
      setError(isUnreachableError(e) ? null : e?.message || String(e));

      if (!warnedRef.current) {
        warnedRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
          "[engine] /status unavailable — falling back to labelled demo data and backing off.",
          e?.code || e?.message || e
        );
      }
      return null;
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  }, [active]);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    const schedule = (ms) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (!cancelled) loop();
      }, ms);
    };

    const loop = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) {
        /* Hidden tab: skip the request, keep the heartbeat so we resume on our
           own even if visibilitychange never fires. */
        schedule(POLL_MS);
        return;
      }
      await refresh();
      if (cancelled) return;
      schedule(failureRef.current > 0 ? backoffMs(failureRef.current) : POLL_MS);
    };

    const onVisibility = () => {
      if (cancelled || (typeof document !== "undefined" && document.hidden)) return;
      loop(); // coming back should show current data, not wait out the interval
    };

    if (typeof document !== "undefined" && document.addEventListener) {
      document.addEventListener("visibilitychange", onVisibility);
    }
    /* ARC-53 — `focus` as well as `visibilitychange`. The slot gate reads
       /status now, and a member who stakes on the Supernova tab and comes
       straight back here has to see the new slot without a reload. An in-app tab
       switch fires neither event reliably on its own, and a browser-tab switch
       fires them in different orders, so both are wired to the same handler and
       the in-flight guard collapses the duplicate. */
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("focus", onVisibility);
    }
    loop();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      if (typeof document !== "undefined" && document.removeEventListener) {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined" && window.removeEventListener) {
        window.removeEventListener("focus", onVisibility);
      }
    };
  }, [active, refresh]);

  const mode = !configured || (!everConnected && !loading) ? "demo" : everConnected ? "live" : "connecting";

  return {
    /** "demo" | "connecting" | "live" — what the values on screen actually are */
    mode,
    configured,
    /** Normalized EngineStatus, or null (demo mode, or enrolled-but-no-record) */
    status,
    loading,
    refreshing,
    error,
    /** Real engine values that stopped refreshing — live, but out of date */
    stale: everConnected && failures > 0,
    failures,
    lastUpdatedMs,
    /** Next attempt's delay, so the UI can say when rather than just "later" */
    retryInMs: failures > 0 ? backoffMs(failures) : null,
    refresh,
  };
}
